import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import './Administrator.css';
import AppPagination from '../../components/base/AppPagination';
import { usePagination } from '../../hooks/usePagination';
import ConfirmDialog from '../../components/base/ConfirmDialog';
import AppSearch from '../../components/base/AppSearch';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

import RoleFilterSelect from './RoleFilterSelect';
import AdminTableRow from './AdminTableRow';
import SessionsDrawer from './SessionsDrawer';
import AdminDetailsDrawer from './AdminDetailsDrawer';
import AddAdminDrawer from './AddAdminDrawer';
import AddIntervenantDrawer from './AddIntervenantDrawer';
import AddPraticienDialog from './AddPraticienDialog';
const Administrator = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [openAddDrawer, setOpenAddDrawer] = useState(false);
  const [addFile, setAddFile] = useState(null);
  const [addPreview, setAddPreview] = useState(null);
  const [newData, setNewData] = useState({
    nom_user: '',
    prenom_user: '',
    email_user: '',
    password_user: '',
    addresse_user: '',
    ville_user: '',
    cp_user: '',
    province_user: '',
    phone_user: '',
    role_user: '2',
    etat_user: 'active',
  });

  const [openAddIntervenantDrawer, setOpenAddIntervenantDrawer] = useState(false);
  const [intervenantFile, setIntervenantFile] = useState(null);
  const [intervenantPreview, setIntervenantPreview] = useState(null);
const [intervenantData, setIntervenantData] = useState({
  nom_user: '',
  prenom_user: '',
  email_user: '',
  password_user: '',
  title_user: '',
  lien_boutique: '',
  commission: '0',
  prix_soa: '0',
  commission_consultation: '0',
  commission_video: '0',
  phone_user: '',
  addresse_user: '',
  ville_user: '',
  cp_user: '',
  province_user: '',
  main_speciality_user: '',
  description_user: '',
  etat_user: 'active',
});

const INITIAL_PRATICIEN = {
  nom_user: '',
  prenom_user: '',
  email_user: '',
  password_user: '',
  addresse_user: '',
  ville_user: '',
  cp_user: '',
  province_user: '',
  phone_user: '',
  description_user: '',
  title_user: '',
  main_speciality_user: '',
  prix_soa: '',
  commission_consultation: '',
  commission_video: '',
  etat_user: 'active',
  create_zoom_account: false, // booléen, toujours défini
};

const [openAddPraticienDialog, setOpenAddPraticienDialog] = useState(false);
const [praticienFile, setPraticienFile] = useState(null);
const [praticienPreview, setPraticienPreview] = useState(null);
const [praticienData, setPraticienData] = useState(INITIAL_PRATICIEN);

const resetPraticienForm = () => {
  setOpenAddPraticienDialog(false);
  setPraticienFile(null);
  setPraticienPreview(null);
  setPraticienData(INITIAL_PRATICIEN);
};

const handleAddPraticienSubmit = async () => {
  setIsSubmitting(true);
  const formData = new FormData();

  Object.entries(praticienData).forEach(([key, value]) => {
    if (key === 'create_zoom_account') {
      // Laravel "boolean" accepte 1/0, pas "true"/"false" en FormData
      formData.append(key, value ? 1 : 0);
    } else if (value !== undefined && value !== null && value !== '') {
      formData.append(key, value);
    }
  });

  if (praticienFile) formData.append('photo_user', praticienFile);

  try {
    const response = await axios.post(
      `${apiUrl}/admin/praticien/a/add_praticien`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data.status === 'success' || response.status === 200) {
      toast.success('Praticien ajouté avec succès !');

      // Avertissement si le compte Zoom a échoué (renvoyé par le back)
      if (response.data.warning) {
        toast.warning(response.data.warning);
      }

      resetPraticienForm();
      fetchAdmins();
    }
  } catch (err) {
    const errorData = err.response?.data;
    if (errorData?.error && typeof errorData.error === 'object') {
      Object.values(errorData.error).forEach((messages) => {
        toast.error(Array.isArray(messages) ? messages[0] : messages);
      });
    } else {
      toast.error(errorData?.error || errorData?.message || "Erreur lors de l'ajout du praticien");
    }
  } finally {
    setIsSubmitting(false);
  }
};

const handlePraticienFileChange = (e) => {
  const file = e.target.files?.[0];
  if (file) {
    setPraticienFile(file);
    setPraticienPreview(URL.createObjectURL(file));
  }
};
 

  // Recherche + filtre par rôle
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const filteredAdmins = useMemo(() => {
    return admins.filter((admin) => {
      const searchStr = searchTerm.toLowerCase();
      const matchesSearch =
        admin.nom_user?.toLowerCase().includes(searchStr) ||
        admin.prenom_user?.toLowerCase().includes(searchStr) ||
        admin.email_user?.toLowerCase().includes(searchStr);

      const matchesRole =
        roleFilter === 'all' || Number(admin.role_user) === Number(roleFilter);

      return matchesSearch && matchesRole;
    });
  }, [admins, searchTerm, roleFilter]);

  const itemsPerPage = 8;
  const { next, currentPage, count } = usePagination(filteredAdmins, itemsPerPage);

  const baseUrl = process.env.REACT_APP_API_URL;
  const storageUrl = `${baseUrl}/storage/workshop`;
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const getFullImageUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('blob:') || photoPath.startsWith('http')) return photoPath;
    return `${storageUrl}/${photoPath}`;
  };

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/admin/la/list_admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.status === 'success') setAdmins(response.data.result || []);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    if (user) fetchAdmins();
  }, [user, fetchAdmins]);

  // ---- Ajout Admin ----
  const handleAddSubmit = async () => {
    if (!addFile) {
      toast.error('La photo est obligatoire');
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    Object.keys(newData).forEach((key) => formData.append(key, newData[key]));
    formData.append('photo_user', addFile);

    try {
      const response = await axios.post(`${apiUrl}/admin/aa/add_admin`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 'success') {
        toast.success('Administrateur ajouté !');
        setOpenAddDrawer(false);
        setAddFile(null);
        setAddPreview(null);
        setNewData({
          nom_user: '',
          prenom_user: '',
          email_user: '',
          password_user: '',
          addresse_user: '',
          ville_user: '',
          cp_user: '',
          province_user: '',
          phone_user: '',
          role_user: '2',
          etat_user: 'active',
        });
        fetchAdmins();
      }
    } catch (err) {
      const errors = err.response?.data?.error;
      if (typeof errors === 'object') {
        Object.values(errors).forEach((msg) => toast.error(msg[0]));
      } else {
        toast.error(err.response?.data?.error || "Erreur lors de l'ajout");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAddFile(file);
      setAddPreview(URL.createObjectURL(file));
    }
  };

  // ---- Ajout Intervenant ----
  const handleAddIntervenantSubmit = async () => {
    setIsSubmitting(true);
    const formData = new FormData();
    Object.keys(intervenantData).forEach((key) => formData.append(key, intervenantData[key]));
    if (intervenantFile) formData.append('photo_user', intervenantFile);

    try {
      const response = await axios.post(
        `${apiUrl}/admin/intervenant/a/add_intervenant`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.message === 'intervenant created successfully') {
        toast.success('Intervenant et compte Zoom créés !');
        setOpenAddIntervenantDrawer(false);
        setIntervenantFile(null);
        setIntervenantPreview(null);
        setIntervenantData({
          nom_user: '',
          prenom_user: '',
          email_user: '',
          password_user: '',
          addresse_user: '',
          ville_user: '',
          cp_user: '',
          province_user: '',
          phone_user: '',
          description_user: '',
          title_user: '',
          main_speciality_user: '',
          lien_boutique: '',
          etat_user: 'active',
        });
        fetchAdmins();
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.error && typeof errorData.error === 'object') {
        Object.values(errorData.error).forEach((messages) => {
          toast.error(Array.isArray(messages) ? messages[0] : messages);
        });
      } else if (typeof errorData?.error === 'string') {
        toast.error(errorData.error);
      } else if (typeof errorData?.message === 'string') {
        toast.error(errorData.message);
      } else {
        toast.error("Erreur lors de la création de l'intervenant");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIntervenantFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIntervenantFile(file);
      setIntervenantPreview(URL.createObjectURL(file));
    }
  };

  // ---- Suppression ----
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenDeleteDialog = (admin) => {
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!adminToDelete) return;

    setIsDeleting(true);
    try {
      const response = await axios.delete(`${apiUrl}/admin/delete/${adminToDelete.id_user}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === 'success') {
        toast.success('Administrateur supprimé avec succès');
        fetchAdmins();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
    }
  };

  // ---- Vue / édition ----
const handleOpenView = (admin) => {
  if (!admin) return;

  setSelectedAdmin(admin);

  setEditData({
    ...admin,

    // On met les valeurs directement au niveau de editData
    prix_soa:
      admin.prix_soa ??
      admin.praticien_info?.prix_soa ??
      '',

    commission_consultation:
      admin.commission_consultation ??
      admin.praticien_info?.commission_consultation ??
      '',

    commission_video:
      admin.commission_video ??
      admin.praticien_info?.commission_video ??
      '',
  });

  setPreviewUrl(admin?.photo_user || null);
  setIsEditMode(false);
  setSelectedFile(null);
  setOpenDrawer(true);
};

  const handleEditChange = (e) => {
  const { name, value } = e.target;

  setEditData((prev) => ({
    ...prev,
    [name]: name === 'availability_user' ? Number(value) : value,
  }));
};

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpdateSubmit = async () => {
  setIsSubmitting(true);

  try {
    const formData = new FormData();

    const fieldsToSkip = [
      'id_user',
      'date_inscrip_user',
      'last_cnx_user',
      'nbr_view',
      'total_reviews_user',
      'praticien_info',
    ];

    // --------------------------------------------------
    // Champs classiques de l'utilisateur
    // --------------------------------------------------
    Object.keys(editData).forEach((key) => {
      if (
        !fieldsToSkip.includes(key) &&
        editData[key] !== null &&
        editData[key] !== undefined
      ) {
        formData.append(key, editData[key]);
      }
    });

    // --------------------------------------------------
    // Prix SOA + commissions
    // Rôles :
    // 3 = Intervenant
    // 4 = Praticien
    // --------------------------------------------------
    const role = Number(editData.role_user);

    if (role === 3 || role === 4) {
      const prixSoa =
        editData.prix_soa ??
        editData.praticien_info?.prix_soa ??
        '';

      const commissionConsultation =
        editData.commission_consultation ??
        editData.praticien_info?.commission_consultation ??
        '';

      const commissionVideo =
        editData.commission_video ??
        editData.praticien_info?.commission_video ??
        '';

      formData.append('prix_soa', prixSoa);
      formData.append(
        'commission_consultation',
        commissionConsultation
      );
      formData.append(
        'commission_video',
        commissionVideo
      );
    }

    // --------------------------------------------------
    // Photo
    // --------------------------------------------------
    if (selectedFile) {
      formData.append('photo_user', selectedFile);
    }

    // --------------------------------------------------
    // Debug temporaire
    // --------------------------------------------------
    console.log('=== DONNÉES ENVOYÉES ===');

    for (const [key, value] of formData.entries()) {
      console.log(key, value);
    }

    // --------------------------------------------------
    // Appel API
    // --------------------------------------------------
    const response = await axios.post(
      `${apiUrl}/admin/update/u/user/${selectedAdmin.id_user}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data.status === 'success') {
      toast.success('Profil mis à jour');

      setOpenDrawer(false);
      setIsEditMode(false);

      await fetchAdmins();
    }
  } catch (err) {
    console.error(
      'Erreur mise à jour utilisateur :',
      err.response?.data || err
    );

    toast.error(
      err.response?.data?.message ||
      err.response?.data?.error ||
      'Erreur de mise à jour'
    );
  } finally {
    setIsSubmitting(false);
  }
};
  // ---- Disponibilité ----
  const [loadingId, setLoadingId] = useState(null);
  const toggleAvailability = async (admin) => {
    const adminId = admin.id_user;
    const oldStatus = Number(admin.availability_user);
    const newStatus = oldStatus === 1 ? 0 : 1;

    setLoadingId(adminId);
    setAdmins((prevAdmins) =>
      prevAdmins.map((item) =>
        item.id_user === adminId ? { ...item, availability_user: newStatus } : item
      )
    );

    try {
      const res = await axios.post(
        `${apiUrl}/admin/intervenant/status/${adminId}`,
        { availability_user: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.result) {
        setAdmins((prevAdmins) =>
          prevAdmins.map((item) =>
            item.id_user === adminId
              ? { ...item, availability_user: Number(res.data.result.availability_user) }
              : item
          )
        );
      }
    } catch (err) {
      setAdmins((prevAdmins) =>
        prevAdmins.map((item) =>
          item.id_user === adminId ? { ...item, availability_user: oldStatus } : item
        )
      );
      console.error(err);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoadingId(null);
    }
  };

  // ---- Sessions ----
  const [openSessionsDrawer, setOpenSessionsDrawer] = useState(false);

  const handleOpenSessions = (admin) => {
    setSelectedAdmin(admin);
    setOpenSessionsDrawer(true);
  };

  // ---- Google Calendar ----
  const handleConnectGoogle = async (adminId) => {
    const response = await axios.get(`${apiUrl}/intervenant/google-calendar/connect`, {
      params: { id_user: adminId },
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.data?.url) {
      const popup = window.open(response.data.url, 'google-oauth', 'width=500,height=650');
      const timer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(timer);
          toast.success('Connexion Google terminée');
          fetchAdmins();
        }
      }, 1000);
    }
  };

  const handleDisconnectGoogle = async (adminId) => {
    try {
      setLoadingId(adminId);
      const response = await axios.delete(`${apiUrl}/admin/google-calendar/disconnect`, {
        params: { id_user: adminId },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status === 'success') {
        toast.success('Google Calendar déconnecté');
        setAdmins((prev) =>
          prev.map((a) =>
            a.id_user === adminId
              ? {
                  ...a,
                  google_calendar_account: { ...a.google_calendar_account, is_connected: false },
                }
              : a
          )
        );
      }
    } catch (err) {
      const apiErrorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Erreur lors de la déconnexion Google Calendar';
      toast.error(apiErrorMessage);
      console.error("Détail de l'erreur API :", err.response?.data);
    } finally {
      setLoadingId(null);
    }
  };

 // ---- Drag & drop ----
  const orderedAdmins = filteredAdmins;

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (roleFilter !== 'all') {
      toast.info('Astuce : le réordonnancement est plus fiable avec le filtre "Tous les rôles".');
    }

    const oldAdmins = [...admins];
    const oldIndex = admins.findIndex((item) => item.id_user === active.id);
    const newIndex = admins.findIndex((item) => item.id_user === over.id);
    const newArray = arrayMove(admins, oldIndex, newIndex);

    setAdmins(newArray);

    try {
      // ✅ Correction ici : on autorise les rôles 3 (Intervenant) ET 4 (Praticien)
      const orderedIds = newArray
        .filter((item) => {
          const role = Number(item.role_user);
          return role === 3 || role === 4; 
        })
        .map((item) => item.id_user);

      await axios.post(
        `${apiUrl}/admin/intervenants/update-positions`,
        { ordered_ids: orderedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Ordre mis à jour');
    } catch (error) {
      setAdmins(oldAdmins);
      toast.error("Erreur lors de la sauvegarde de l'ordre");
      console.error(error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
        flexWrap="wrap"
        rowGap={2}
      >
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#2B3674' }}>
          Staff & Accès
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={1}>
          <AppSearch value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher un admin..." />

          <RoleFilterSelect value={roleFilter} onChange={setRoleFilter} />

          <Button
            variant="outlined"
            onClick={() => setOpenAddDrawer(true)}
            sx={{ borderRadius: '12px', fontWeight: 700, borderColor: '#4318FF', color: '#4318FF' }}
          >
            + Admin
          </Button>
          <Button
            variant="contained"
            onClick={() => setOpenAddIntervenantDrawer(true)}
            sx={{
              bgcolor: '#4318FF',
              borderRadius: '20px',
              px: 6,
              py: 1.5,
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              boxShadow: '0px 4px 12px rgba(67, 24, 255, 0.2)',
              '&:hover': { bgcolor: '#3311CC', boxShadow: 'none' },
            }}
          >
            + Intervenant
          </Button>

          <Button
            variant="outlined"
            onClick={() => setOpenAddPraticienDialog(true)}
            sx={{ borderRadius: '12px', fontWeight: 700, borderColor: '#4318FF', color: '#4318FF' }}
          >
            + Praticien
          </Button>
        </Stack>
      </Stack>

      <Paper elevation={0} className="admin-table-container">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell className="table-header-cell">MEMBRE</TableCell>
                <TableCell className="table-header-cell">RÔLE</TableCell>
                <TableCell className="table-header-cell">DISPONIBILITÉ</TableCell>
                <TableCell className="table-header-cell">GOOGLE CALENDAR</TableCell>
                <TableCell className="table-header-cell">Durée de connexion</TableCell>
                <TableCell align="right" className="table-header-cell">
                  ACTIONS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={30} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : orderedAdmins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography sx={{ color: '#A3AED0', py: 3 }}>
                      Aucun résultat pour ce filtre.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={orderedAdmins.map((a) => a.id_user)}
                    strategy={verticalListSortingStrategy}
                  >
                    {orderedAdmins.filter(Boolean).map((admin) => (
                      <AdminTableRow
                        key={admin.id_user}
                        admin={admin}
                        getFullImageUrl={getFullImageUrl}
                        loadingId={loadingId}
                        onToggleAvailability={toggleAvailability}
                        onConnectGoogle={handleConnectGoogle}
                        onDisconnectGoogle={handleDisconnectGoogle}
                        onOpenSessions={handleOpenSessions}
                        onOpenView={handleOpenView}
                        onOpenDelete={handleOpenDeleteDialog}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <SessionsDrawer
          open={openSessionsDrawer}
          onClose={() => setOpenSessionsDrawer(false)}
          admin={selectedAdmin}
          getFullImageUrl={getFullImageUrl}
        />

        {!loading && filteredAdmins.length > 0 && (
          <AppPagination
            count={count}
            currentPage={currentPage}
            onChange={next}
            totalItems={filteredAdmins.length}
            itemsPerPage={itemsPerPage}
          />
        )}
      </Paper>

      <AdminDetailsDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        selectedAdmin={selectedAdmin}
        editData={editData}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        onEditChange={handleEditChange}
        onFileChange={handleFileChange}
        previewUrl={previewUrl}
        setPreviewUrl={setPreviewUrl}
        getFullImageUrl={getFullImageUrl}
        onSubmit={handleUpdateSubmit}
        isSubmitting={isSubmitting}
      />

      <AddAdminDrawer
        open={openAddDrawer}
        onClose={() => setOpenAddDrawer(false)}
        newData={newData}
        setNewData={setNewData}
        addPreview={addPreview}
        onFileChange={handleAddFileChange}
        onSubmit={handleAddSubmit}
        isSubmitting={isSubmitting}
      />

      <AddIntervenantDrawer
        open={openAddIntervenantDrawer}
        onClose={() => setOpenAddIntervenantDrawer(false)}
        intervenantData={intervenantData}
        setIntervenantData={setIntervenantData}
        intervenantPreview={intervenantPreview}
        onFileChange={handleIntervenantFileChange}
        onSubmit={handleAddIntervenantSubmit}
        isSubmitting={isSubmitting}
      />
<AddPraticienDialog
        open={openAddPraticienDialog}
        onClose={() => setOpenAddPraticienDialog(false)}
        praticienData={praticienData}
        setPraticienData={setPraticienData}
        praticienPreview={praticienPreview}
        onFileChange={handlePraticienFileChange}
        onSubmit={handleAddPraticienSubmit}
        isSubmitting={isSubmitting}
      />
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Supprimer ce membre ?"
        message={
          adminToDelete
            ? `Êtes-vous sûr de vouloir supprimer ${adminToDelete.prenom_user} ${adminToDelete.nom_user} ? Cette action est irréversible.`
            : ''
        }
        loading={isDeleting}
      />
    </Box>
  );
};

export default Administrator;
